import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Box,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import { 
  Security, 
  Warning, 
  Info, 
  Close,
  Shield,
  Lock,
  Visibility,
  VisibilityOff 
} from '@mui/icons-material';

const SecurityAlert = ({ type = 'info', onDismiss, persistent = false }) => {
  const [dismissed, setDismissed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Auto-dismiss non-persistent alerts after 10 seconds
    if (!persistent) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [persistent]);

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  if (dismissed) return null;

  const getAlertConfig = () => {
    switch (type) {
      case 'password-security':
        return {
          severity: 'info',
          icon: <Lock />,
          title: 'Password Security Tips',
          message: 'Your password is encrypted and never stored in plain text.',
          details: [
            '• Use a unique password for this account',
            '• Consider using a password manager',
            '• Enable two-factor authentication when available',
            '• Never share your password with others'
          ]
        };
      
      case 'account-security':
        return {
          severity: 'success',
          icon: <Shield />,
          title: 'Account Security',
          message: 'Your account is protected with industry-standard security.',
          details: [
            '• All data is encrypted in transit and at rest',
            '• We use AWS Cognito for secure authentication',
            '• Your email will never be shared with third parties',
            '• You can delete your account anytime'
          ]
        };
      
      case 'privacy-notice':
        return {
          severity: 'info',
          icon: <Info />,
          title: 'Privacy Notice',
          message: 'We respect your privacy and protect your personal information.',
          details: [
            '• We only collect necessary information',
            '• Your data is never sold to third parties',
            '• You control your privacy settings',
            '• Read our Privacy Policy for full details'
          ]
        };
      
      case 'suspicious-activity':
        return {
          severity: 'warning',
          icon: <Warning />,
          title: 'Security Alert',
          message: 'We detected unusual activity. Please verify your identity.',
          details: [
            '• Multiple failed login attempts detected',
            '• If this wasn\'t you, change your password immediately',
            '• Consider enabling additional security measures',
            '• Contact support if you need help'
          ]
        };
      
      default:
        return {
          severity: 'info',
          icon: <Security />,
          title: 'Security Information',
          message: 'Your security is our priority.',
          details: []
        };
    }
  };

  const config = getAlertConfig();

  return (
    <Alert 
      severity={config.severity}
      icon={config.icon}
      sx={{ 
        mb: 2,
        '& .MuiAlert-message': { width: '100%' }
      }}
      action={
        <Box>
          {config.details.length > 0 && (
            <IconButton
              size="small"
              onClick={() => setShowDetails(!showDetails)}
              sx={{ mr: 1 }}
            >
              {showDetails ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={handleDismiss}
          >
            <Close />
          </IconButton>
        </Box>
      }
    >
      <AlertTitle>{config.title}</AlertTitle>
      <Typography variant="body2" sx={{ mb: showDetails ? 1 : 0 }}>
        {config.message}
      </Typography>
      
      <Collapse in={showDetails}>
        <Box sx={{ mt: 1 }}>
          {config.details.map((detail, index) => (
            <Typography 
              key={index} 
              variant="body2" 
              sx={{ 
                fontSize: '0.8rem',
                color: 'text.secondary',
                lineHeight: 1.4
              }}
            >
              {detail}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Alert>
  );
};

export default SecurityAlert;